<script setup>
import { ControlValidateClass } from '@/setup/validateControl.js';
import { defineProps, ref, onMounted, defineExpose, nextTick } from 'vue';
import { CompareValue } from '@/mixins/component/validateText.js';
import { ValidateRules } from '@/setup/validateControl.js';

const props = defineProps({
  /**
   * Các rules để validate.
   * 1 rule là 1 object gồm:
   * name: tên rule.
   * compareValue: mẫu để so sánh sử dụng khi name = pattern
   * customValidate: hàm custom validate, nhận vào 3 tham số name, controlValue, compareValue.
   * isValidateWhenOutFocus: xác định xem có validate khi outfocus hay ko (Được dùng cho customValidate) Nếu call API để validate thì ko nên xét giá trị này là true vì mỗi lần outfucus sẽ call API 1 lần.
   */
  rules: {
    type: Array,
    validator(value) {
      if (value.length) {
        return value.every((item) => {
          // name phải là các tên đc định nghĩa trong ValidateRules
          if (
            (item.name !== undefined && !ValidateRules[item.name]) ||
            (item.name === ValidateRules.custom && typeof item.customValidate !== 'function') // Nếu là custom thì phải cung cấp hàm custom
          ) {
            return false;
          }
          // compareValue phải là các tên đc định nghĩa trong CompareValue nếu item.name = ValidateRules.pattern
          if (
            item.compareValue !== undefined &&
            item.name === ValidateRules.pattern &&
            !CompareValue[item.compareValue]
          ) {
            return false;
          }
          // customValidate phải là function
          if (
            item.customValidate !== undefined &&
            typeof item.customValidate !== 'function'
          ) {
            return false;
          }
          // isValidateWhenOutFocus là null, undefined hoặc boolean
          if (
            item.customValidate !== undefined &&
            item.customValidate !== null &&
            typeof item.customValidate !== 'boolean'
          ) {
            return false;
          }
          return true;
        });
      }
      return true;
    },
    default: () => [],
  },

  /**
   * Có validate control bị disabled ko
   */
  isValidateDisabledControl: {
    type: Boolean,
    default: false,
  }
});

/**
 * Danh sách các input
 */
const inputList = ref([]);
/**
 * Ref của component
 */
const msValidate = ref(null);
/**
 * Danh sách lỗi
 */
const errorList = ref([]);

const errorFocusable = ref(true);

/**
 * Lấy danh sách các input
 * @author: BMThang(12/11/2022)
 */
const getInputList = () => {
  const msControls = [];
  
  if (msValidate.value) {
    // Lấy các thẻ có class ControlValidateClass
    const msControlElments = msValidate.value.querySelectorAll(
      `.${ControlValidateClass}`
    );
    const msControlElmentsCount = msControlElments.length;
    if (msControlElmentsCount) {
      // Lấy các component chứa các element ở trên
      for (let index = 0; index < msControlElmentsCount; index++) {
        const msControlElement = msControlElments[index];
        if (typeof msControlElement.getVueInstance === 'function') {
          // Hàm getVueInstance đc khai báo ở global để lấy instance của component chứa element
          const vueObj = msControlElement.getVueInstance();
          msControls.push(vueObj);
        }
      }
    }
  }
  return msControls;
};

/**
 * Xóa danh sách lỗi
 * @author: BMThang(12/11/2022)
 */
const clearErrorList = () => {
  errorList.value = [];
};

/**
 * Hàm dùng để focus vào ô lỗi đầu tiên
 * @author: BMThang(12/11/2022)
 *  
 */
const focusFirstError = () => {
  let listError = errorList.value;
  if (listError.length <= 0) {
    return;
  }
  if (listError[0].gridIns) {
    // Nếu trong grid
    listError[0].gridIns.cellSelected(listError[0].element);
  } else {
    // Nếu là element thông thường
    if (!listError[0].element?.errorElement) {
      return;
    }
    listError[0].element.errorElement.scrollIntoView({
      behavior: "smooth",
      block: "start",
    });
    let scrollTimeout;
    let intersectionObserver = new IntersectionObserver((entries) => {
      let [entry] = entries;
      if (entry.isIntersecting) {
        clearTimeout(scrollTimeout);
        scrollTimeout = setTimeout(() => {
          if (errorFocusable.value) {
            if (typeof listError[0].element.focusError === 'function') {
              listError[0].element.focusError();
            }
            errorFocusable.value = false;
          }
        }, 350);
      }
    });
    // start observing
    intersectionObserver.observe(listError[0].element.errorElement);
  }
};

/**
 * Xử lý validate các input
 * @param {boolean} force Có bắt buộc validate toàn bộ hay ko. Nếu ko chỉ validate các input có trạng thái invalid
 * @param {string[]} exceptedIds Danh sách id component, nếu trong danh sách này thì sẽ ko bị validate
 * @author: BMThang(12/11/2022)
 * @returns true nếu valid ngược lại false
 */
const validate = async (force = false, exceptedIds = []) => {
  clearErrorList();
  let isValid = true;
  errorFocusable.value = true;
  inputList.value = getInputList();
  const inputListCount = inputList.value.length;

  for (let index = 0; index < inputListCount; index++) {
    const input = inputList.value[index];
    
    // Nếu có trong danh sách loại trừ thì ko cần validate
    if (input.componentId) {
      if (exceptedIds.includes(input.componentId)) {
        continue;
      }
    }

    if (
      typeof input.validate === 'function' && // input có hàm validate
      (props.isValidateDisabledControl ? true : !input.disabled) // input ko bị disabled
    ) {
      let validateProvider = input.errorProvider || {};
      if (!validateProvider.isValid || force) {
        validateProvider = await input.validate(true, exceptedIds);
      }

      // Nếu có lỗi thì thêm vào danh sách lỗi
      if (Array.isArray(validateProvider)) {
        validateProvider.forEach((validateResult) => {
          if (!validateResult.isValid) {
            errorList.value.push(validateResult);
            isValid = false;
          }
        });
      } else if (validateProvider && !validateProvider.isValid) {
        errorList.value.push(validateProvider);
        isValid = false;
      }
    }
  }
  if (errorList.value.length && !isValid) {
    nextTick(() => {
      focusFirstError();
    });
    return false;
  }
  return true;
};

onMounted(() => {
  inputList.value = getInputList();
});

defineExpose({
  validate,
});
</script>

<template>
  <div ref="msValidate" class="ms-validate">
    <slot />
  </div>
</template>
